import React, {FC, useEffect, useState} from "react"

import Flex from "@irati/ui/src/Flex"
import {Body, H6} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import theme from "@irati/ui/src/global/theme"
import {Select} from "@irati/ui/src/FormItem"
import {CelebrationsReportDuration, CelebrationsReportCategory, CelebrationsReport, Celebration, CompanyValue} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {Row} from "@irati/ui/src/Layout"
import {RouterLink} from "@irati/ui/src/Link"
import appsConfig from "@irati-web/app/src/routeConfig"

import Spacing from "@irati/ui/src/Spacing"
import TeamSearch from "@irati/ui/src/TeamSearch"
import Region, {ALL_TEAMS} from "../../Region"
import {EmptyData} from "../../EmptyData"
import {SETTINGS_CUSTOMISE_VALUES} from "../../../../routes/routeConfig"
import buildAvailableDatesSelectOptions from "../helpers/buildAvailableDatesSelectOptions"
import buildMonthAndDurationFields from "../helpers/buildMonthAndDurationFields"
import ValuesAndCelebrationsCard from "./Card"

interface Option {
  value: string
  label: string
}

interface Props extends InjectIntl {}

const ValuesAndCelebrations: FC<Props> = ({formatMessage}) => {
  const [regionId, setRegionId] = useState(ALL_TEAMS)
  const [timeRangeOptions, setTimeRangeOptions] = useState<Option[]>([])
  const [duration, setDuration] = useState<CelebrationsReportDuration>(CelebrationsReportDuration.CelebrationsLast_90Days)
  const [teamId, setTeamId] = useState(ALL_TEAMS)

  const [values, setValues] = useState<CompanyValue[]>()
  const [celebrations, setCelebrations] = useState<Celebration[]>()
  const [load, setLoad] = useState<Promise<void> | null>(null)

  useEffect(() => {
    const getAvailableDates = async () => {
      const availableDates = await getValuesAndCelebrationsReport()
      const selectOptions = buildAvailableDatesSelectOptions(availableDates, formatMessage)
      setTimeRangeOptions(selectOptions)
    }
    getAvailableDates()
  }, [])

  useEffect(() => {
    getValuesAndCelebrationsReport()
  }, [regionId, teamId, duration])

  const getValuesAndCelebrationsReport = () => {
    const regionIds = regionId !== ALL_TEAMS ? [regionId] : undefined
    const teamIds = teamId !== ALL_TEAMS ? [teamId] : undefined
    const monthAndDuration = buildMonthAndDurationFields(duration)

    const loaded = adminReportingService
      .getCelebrationsReport({
        businessId: authentication.business().uuid,
        regionIds,
        teamIds,
        duration: monthAndDuration.duration,
        category: CelebrationsReportCategory.CompanyValuesAndCelebrations,
        month: monthAndDuration.month,
        limit: 5
      })
      .then((data: CelebrationsReport) => {
        setValues(data?.companyValuesAndCelebrations?.companyValues)
        setCelebrations(data?.companyValuesAndCelebrations?.celebrations)
        return data.availableDates
      })
    setLoad(loaded)
    return loaded
  }

  return (
    <>
      <Flex justifyContent="space-between" marginBottom={4}>
        <Flex direction="column">
          <H6 marginBottom>
            <FormattedMessage id="admin.reporting.celebrations.valuesAndCelebrations.title" />
          </H6>
          <Body small>
            <FormattedMessage id="admin.reporting.celebrations.valuesAndCelebrations.subTitle" />
          </Body>
        </Flex>
        <Flex>
          <Region id="adminSection_valuesAndCelebrations_country" value={regionId} onChange={(country: string) => setRegionId(country)} />
          <TeamSearch value={teamId} onChange={(team: string) => setTeamId(team)} />
          <Row>
            <Select
              id="adminSection_valuesAndCelebrations_timePeriod"
              name="timeRangeTopValuesAndCelebrations"
              options={timeRangeOptions}
              value={duration}
              onChange={(time: CelebrationsReportDuration) => setDuration(time)}
            />
          </Row>
        </Flex>
      </Flex>
      <Loading load={load}>
        <Flex width="100%" gap={theme.spacing.two}>
          {values?.length > 0 && (
            <ValuesAndCelebrationsCard title="admin.reporting.celebrations.valuesAndCelebrations.values.title" data={values} />
          )}
          {celebrations && celebrations.length > 0 && (
            <ValuesAndCelebrationsCard title="admin.reporting.celebrations.valuesAndCelebrations.celebrations.title" data={celebrations} />
          )}
        </Flex>
        {((values?.length === 0 && celebrations?.length === 0) || (!values && !celebrations)) && (
          <EmptyData text="admin.reporting.celebrations.empty.text">
            <Spacing marginBottom={2}>
              <RouterLink to="/" app={appsConfig.RECOGNITION}>
                <FormattedMessage id="admin.reporting.celebrations.valuesAndCelebrations.empty.btn" />
              </RouterLink>
            </Spacing>
            <RouterLink to={SETTINGS_CUSTOMISE_VALUES}>
              <FormattedMessage id="admin.reporting.celebrations.valuesAndCelebrations.empty.btn2" />
            </RouterLink>
          </EmptyData>
        )}
      </Loading>
    </>
  )
}

export default injectIntl(ValuesAndCelebrations)
